import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Camera, Folder, Image, Video, Download, Share2, Trash2, Search, Grid3x3, List, ArrowLeft, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const AssetVault = () => {
  const { bookingId } = useParams();
  const [viewMode, setViewMode] = React.useState('grid'); // 'grid' or 'list'
  const [selectedAssets, setSelectedAssets] = React.useState([]);

  const folders = [
    { id: 1, name: 'Wedding - Sarah & David', date: 'June 15, 2024', assetCount: 487 },
    { id: 2, name: 'Product Shoot - TechCo', date: 'July 3, 2024', assetCount: 156 },
    { id: 3, name: 'Family Portrait', date: 'August 10, 2024', assetCount: 89 },
  ];

  const assets = [
    { id: 1, type: 'image', name: 'IMG_0001.jpg', size: '8.4 MB', url: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=400&h=300&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=400&h=300&fit=crop' },
    { id: 2, type: 'image', name: 'IMG_0002.jpg', size: '7.2 MB', url: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=400&h=300&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=400&h=300&fit=crop' },
    { id: 3, type: 'image', name: 'IMG_0003.jpg', size: '9.1 MB', url: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=400&h=300&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=400&h=300&fit=crop' },
    { id: 4, type: 'video', name: 'VID_0001.mp4', size: '245 MB', url: '#', thumbnail: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=400&h=300&fit=crop' },
    { id: 5, type: 'image', name: 'IMG_0004.jpg', size: '6.8 MB', url: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=400&h=300&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=400&h=300&fit=crop' },
    { id: 6, type: 'image', name: 'IMG_0005.jpg', size: '8.9 MB', url: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=400&h=300&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=400&h=300&fit=crop' },
  ];

  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev =>
      prev.includes(assetId)
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/client/dashboard" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest Vault</span>
            </Link>
            <div className="flex items-center space-x-4">
              {selectedAssets.length > 0 && (
                <Badge variant="secondary" data-testid="selection-count">
                  {selectedAssets.length} selected
                </Badge>
              )}
              <Link to="/client/dashboard">
                <Button variant="ghost" size="sm" data-testid="dashboard-link">Dashboard</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-muted-foreground mb-6" data-testid="breadcrumb">
          <Link to="/client/dashboard" className="hover:text-foreground">My Bookings</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground" data-testid="current-folder">Wedding - Sarah & David</span>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search assets..."
              className="pl-12 bg-muted/50"
              data-testid="search-assets"
            />
          </div>

          <div className="flex items-center gap-2">
            {selectedAssets.length > 0 && (
              <>
                <Button variant="outline" size="sm" data-testid="download-selected">
                  <Download className="w-4 h-4 mr-2" />
                  Download ({selectedAssets.length})
                </Button>
                <Button variant="outline" size="sm" data-testid="share-selected">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                <Button variant="outline" size="sm" data-testid="delete-selected">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
            <div className="border-l border-border pl-2 ml-2 flex gap-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('grid')}
                data-testid="view-grid"
              >
                <Grid3x3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('list')}
                data-testid="view-list"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-6 mb-8 text-sm text-muted-foreground" data-testid="vault-stats">
          <div>487 items</div>
          <div>•</div>
          <div>3.2 GB total</div>
          <div>•</div>
          <div>Last updated 2 days ago</div>
        </div>

        {/* Grid View */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1" data-testid="asset-grid">
            {assets.map((asset) => (
              <div
                key={asset.id}
                className="relative group aspect-square bg-muted/30 cursor-pointer overflow-hidden"
                onClick={() => toggleAssetSelection(asset.id)}
                data-testid={`asset-${asset.id}`}
              >
                <img
                  src={asset.thumbnail}
                  alt={asset.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {asset.type === 'video' && (
                  <div className="absolute top-2 left-2">
                    <Badge variant="secondary" className="bg-black/70 text-white" data-testid={`video-badge-${asset.id}`}>
                      <Video className="w-3 h-3 mr-1" />
                      Video
                    </Badge>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                    <div className="text-sm font-medium truncate" data-testid={`asset-name-${asset.id}`}>{asset.name}</div>
                    <div className="text-xs opacity-75" data-testid={`asset-size-${asset.id}`}>{asset.size}</div>
                  </div>
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex gap-1">
                    <Button size="icon" variant="secondary" className="h-8 w-8 bg-black/70 hover:bg-black/90" data-testid={`preview-${asset.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="secondary" className="h-8 w-8 bg-black/70 hover:bg-black/90" data-testid={`download-${asset.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {selectedAssets.includes(asset.id) && (
                  <div className="absolute inset-0 border-4 border-primary bg-primary/10" data-testid={`selected-${asset.id}`} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="space-y-1" data-testid="asset-list">
            {assets.map((asset) => (
              <div
                key={asset.id}
                className={`flex items-center gap-4 p-4 rounded-sm hover:bg-muted/30 cursor-pointer transition-colors ${
                  selectedAssets.includes(asset.id) ? 'bg-primary/10' : ''
                }`}
                onClick={() => toggleAssetSelection(asset.id)}
                data-testid={`asset-list-${asset.id}`}
              >
                <img
                  src={asset.thumbnail}
                  alt={asset.name}
                  className="w-16 h-16 object-cover rounded-sm"
                />
                <div className="flex-1">
                  <div className="font-medium" data-testid={`list-name-${asset.id}`}>{asset.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {asset.type === 'video' ? <Video className="w-4 h-4 inline mr-1" /> : <Image className="w-4 h-4 inline mr-1" />}
                    {asset.size}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" data-testid={`list-preview-${asset.id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`list-download-${asset.id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`list-share-${asset.id}`}>
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetVault;